import { useAuth } from "core/context/auth"
import { SuperAdminDashboardLoggerAPIDataSourceImpl } from "data/API/SuperAdmin/SuperAdminLoggerDashboardAPIDataSourceImpl"
import { SuperAdminLoggerDashboardRepositoryImpl } from "data/repository/SuperAdmin/SuperAdminLoggerDashboardRepositoryImpl"
import { getAdminLogs } from "domain/useCase/SuperAdmin/SuperAdminLoggerDashboard/GetAdminLogs"
import { useState } from "react"

export default function SuperAdminLoggerDashboardViewModel() {
  const getAdminLogsUseCase = new getAdminLogs(
    new SuperAdminLoggerDashboardRepositoryImpl(new SuperAdminDashboardLoggerAPIDataSourceImpl())
  )

  const superAdminLoggerTableHeaders = [
    "Action",
    "API Endpoint",
    "Method",
    "User ID",
    "Status",
    "Date & Time",
    "Details",
  ]

  const { auth } = useAuth()
  const [adminLogs, setAdminLogs] = useState<any[]>([])
  const [showLoggerModal, setShowLoggerModal] = useState(false)
  const [apiEndpoint, setApiEndpoint] = useState<any>(null)
  const [loggerDetails, setLoggerDetails] = useState("")
  const [searchTrigger, setSearchTrigger] = useState(false)
  const getDefaultDateRange = () => {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 7)
    return {
      startTimestamp: Math.floor(startDate.getTime() / 1000),
      endTimestamp: Math.floor(new Date().getTime() / 1000),
    }
  }

  const defaultDates = getDefaultDateRange()
  const [fromDate, setFromDate] = useState<number | null>(defaultDates.startTimestamp)
  const [toDate, setToDate] = useState<number | null>(defaultDates.endTimestamp)
  const [pageNo, setPageNo] = useState(1)
  const [loading, setLoading] = useState(false)
  const [exhausted, setExhausted] = useState(false)
  const [maxPages, setMaxPages] = useState(1)
  const [lastFetchedPage, setLastFetchedPage] = useState<number>(1)

  const getAllLogs = async () => {
    const details = {
      pageNo: pageNo,
      pageSize: 300,
      startDate: fromDate || defaultDates.startTimestamp,
      endDate: toDate || defaultDates.endTimestamp,
    }

    setLoading(true)
    try {
      const response = await getAdminLogsUseCase.invoke(auth, details)

      if (response?.data?.logs) {
        let filteredLogs = response.data.logs
        if (apiEndpoint === null && lastFetchedPage === pageNo) {
          setLastFetchedPage(1)
          setPageNo(1)
          setAdminLogs(response.data.logs)

          return response.data.logs
        } else {
          if (apiEndpoint) {
            const searchTerm = apiEndpoint.toLowerCase()
            filteredLogs = filteredLogs.filter((log: any) => log.apiEndpoint.toLowerCase().includes(searchTerm))
            setAdminLogs((prev) => [
              ...prev,
              ...filteredLogs.filter((newLog: any) => !prev.some((existingLog) => existingLog.id === newLog.id)),
            ])
          } else if (pageNo === 1 || searchTrigger) {
            setAdminLogs((prev) => [...prev, ...filteredLogs])
          } else {
            setAdminLogs((prev) => [
              ...prev,
              ...filteredLogs.filter((newLog: any) => !prev.some((existingLog) => existingLog.id === newLog.id)),
            ])
          }

          setLastFetchedPage(pageNo)
          setMaxPages(response.data.totalPages)
        }
      }
    } catch (error) {
      console.error("Error fetching logs:", error)
    } finally {
      setLoading(false)
      // setIsInitialLoad(false)
    }
  }
  const showPayload = (requestData: any) => {
    const data = requestData
    const formattedData = JSON.stringify(data, null, 2)
    return formattedData
  }

  function handleDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    const dateValue = e.target.value
    const timestamp = dateValue ? new Date(dateValue).getTime() / 1000 : null
    if (e.target.id === "fromDate") {
      setFromDate(timestamp)
    } else if (e.target.id === "toDate") {
      setToDate(timestamp)
    }
  }

  const convertTimestamp = (timestamp: any) => {
    const { _seconds } = timestamp
    const date = new Date(_seconds * 1000)
    const day = String(date.getDate()).padStart(2, "0") // Ensure 2-digit day
    const month = String(date.getMonth() + 1).padStart(2, "0") // Months are 0-indexed
    const year = date.getFullYear()
    const formattedDate = `${day}/${month}/${year}`
    return formattedDate
  }

  const loadMoreHandler = () => {
    if (pageNo <= maxPages) {
      setLoading(true)
      setPageNo(pageNo + 1)
    } else {
      setExhausted(true)
    }
  }

  const clearFilterHandler = () => {
    setFromDate(defaultDates.startTimestamp)
    setToDate(defaultDates.endTimestamp)
    setPageNo(1)
    setExhausted(false)
    setLoading(false)
    setAdminLogs([])
    setApiEndpoint(null)
    setLastFetchedPage(1)
    getAllLogs()
  }

  const handleSearch = () => {
    setPageNo(1)
    setExhausted(false)
    setAdminLogs([])
    setSearchTrigger((prev) => !prev)
  }

  return {
    handleSearch,
    searchTrigger,
    adminLogs,
    setAdminLogs,
    getAllLogs,
    superAdminLoggerTableHeaders,
    showLoggerModal,
    setShowLoggerModal,
    setLoggerDetails,
    loggerDetails,
    showPayload,
    convertTimestamp,
    toDate,
    fromDate,
    handleDateChange,
    pageNo,
    setPageNo,
    loadMoreHandler,
    exhausted,
    loading,
    setExhausted,
    clearFilterHandler,
    setApiEndpoint,
    apiEndpoint,
  }
}
